/**
 * Lightweight audio-cue layer for the active workout.
 *
 * Two cue modes, both designed to mix over the user's background music:
 *   - 'beeps' — short WebAudio oscillator tones (mixes natively on Android &
 *               desktop; on iOS Safari it briefly dips music).
 *   - 'voice' — SpeechSynthesisUtterance via the platform TTS engine.
 *
 * Cue "types" are semantic — the caller says `cue('countdown_3')`, this module
 * decides the right tone/voice line. All calls are no-ops in 'off' mode.
 *
 * State (mode, defaultRestSeconds, volume) is stored in localStorage and
 * exposed via a tiny pub/sub store so any React component can subscribe with
 * `useSyncExternalStore` — no React Context, no stale closures.
 */

const MODES = ['off', 'beeps', 'voice'];
const MODE_KEY = 'gymflow-audio-mode';
const REST_KEY = 'gymflow-audio-rest';
const VOL_KEY = 'gymflow-audio-volume';

export const AUDIO_MODES = MODES;

function readPrefs() {
    if (typeof window === 'undefined') {
        return { mode: 'voice', defaultRestSeconds: 90, volume: 0.7 };
    }
    let mode = null;
    let rest = NaN;
    let vol = NaN;
    try {
        mode = window.localStorage.getItem(MODE_KEY);
        rest = parseInt(window.localStorage.getItem(REST_KEY) || '', 10);
        vol = parseFloat(window.localStorage.getItem(VOL_KEY) || '');
    } catch {
        /* ignore — private mode or quota */
    }
    return {
        mode: MODES.includes(mode) ? mode : 'voice',
        defaultRestSeconds:
            Number.isFinite(rest) && rest >= 10 && rest <= 600 ? rest : 90,
        volume: Number.isFinite(vol) ? Math.min(1, Math.max(0, vol)) : 0.7,
    };
}

function writePref(key, value) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(key, String(value));
    } catch {
        /* ignore */
    }
}

/* -------------------- Pub/Sub store -------------------- */

let _state = readPrefs();
const _listeners = new Set();

function notify() {
    _listeners.forEach((fn) => {
        try {
            fn();
        } catch {
            /* one listener should not break the others */
        }
    });
}

export function getAudioPrefs() {
    return _state;
}

export function subscribeAudioPrefs(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}

export function setMode(mode) {
    if (!MODES.includes(mode)) return;
    writePref(MODE_KEY, mode);
    _state = { ..._state, mode };
    notify();
    // Best-effort unlock — must run AFTER state update so a thrown error here
    // can never block the toggle.
    try {
        unlockAudio();
    } catch {
        /* ignore */
    }
}

export function setDefaultRest(seconds) {
    const n = Math.max(10, Math.min(600, Math.round(Number(seconds) || 90)));
    writePref(REST_KEY, n);
    _state = { ..._state, defaultRestSeconds: n };
    notify();
}

export function setVolume(volume) {
    const v = Math.min(1, Math.max(0, Number(volume) || 0));
    writePref(VOL_KEY, v);
    _state = { ..._state, volume: v };
    notify();
}

// Cross-tab sync — keep store coherent if the user opens GymFlow in a 2nd tab.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (
            e.key === MODE_KEY ||
            e.key === REST_KEY ||
            e.key === VOL_KEY
        ) {
            _state = readPrefs();
            notify();
        }
    });
}

/* -------------------- WebAudio: beeps -------------------- */

let _ctx = null;
function ctx() {
    if (typeof window === 'undefined') return null;
    if (_ctx && _ctx.state !== 'closed') return _ctx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try {
        _ctx = new AC();
    } catch {
        return null;
    }
    return _ctx;
}

/** Some browsers (iOS Safari, Chrome autoplay) require a user gesture to
 *  unlock the AudioContext. Call from any tap handler before the first cue. */
export async function unlockAudio() {
    const c = ctx();
    if (!c) return;
    try {
        if (c.state === 'suspended') await c.resume();
    } catch {
        /* no-op */
    }
    // Prime SpeechSynthesis (first utterance is often dropped).
    try {
        if (typeof window !== 'undefined' && window.speechSynthesis) {
            const u = new window.SpeechSynthesisUtterance(' ');
            u.volume = 0;
            window.speechSynthesis.speak(u);
        }
    } catch {
        /* no-op */
    }
}

function tone({ freq = 880, ms = 140, type = 'sine', volume = 0.7 }) {
    const c = ctx();
    if (!c) return;
    try {
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        const now = c.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(volume, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + ms / 1000);
        osc.connect(gain).connect(c.destination);
        osc.start(now);
        osc.stop(now + ms / 1000 + 0.02);
    } catch {
        /* swallow — audio is best-effort */
    }
}

/* -------------------- SpeechSynthesis: voice -------------------- */

function speak(text, { volume = 0.9, rate = 1.05 } = {}) {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
        // Cancel any pending utterance so countdowns don't pile up.
        window.speechSynthesis.cancel();
        const u = new window.SpeechSynthesisUtterance(text);
        u.volume = volume;
        u.rate = rate;
        u.pitch = 1.0;
        window.speechSynthesis.speak(u);
    } catch {
        /* swallow */
    }
}

/* -------------------- Public cue API -------------------- */

const CUE_TABLE = {
    set_complete: {
        beep: () => {
            tone({ freq: 660, ms: 90, volume: 0.6 });
            setTimeout(() => tone({ freq: 990, ms: 140, volume: 0.7 }), 110);
        },
        voice: (target) =>
            speak(
                target
                    ? `Set complete. ${target} second rest.`
                    : 'Set complete.'
            ),
    },
    countdown_15: { beep: () => tone({ freq: 520, ms: 100, volume: 0.5 }), voice: () => speak('Fifteen') },
    countdown_10: { beep: () => tone({ freq: 600, ms: 110, volume: 0.55 }), voice: () => speak('Ten') },
    countdown_5: { beep: () => tone({ freq: 700, ms: 110, volume: 0.6 }), voice: () => speak('Five') },
    countdown_3: { beep: () => tone({ freq: 780, ms: 100, volume: 0.65 }), voice: () => speak('Three') },
    countdown_2: { beep: () => tone({ freq: 860, ms: 100, volume: 0.65 }), voice: () => speak('Two') },
    countdown_1: { beep: () => tone({ freq: 940, ms: 100, volume: 0.7 }), voice: () => speak('One') },
    go: {
        beep: () => tone({ freq: 1320, ms: 240, type: 'triangle', volume: 0.8 }),
        voice: () => speak('Go!', { rate: 1.0 }),
    },
    session_start: {
        // Rising 3-tone "ready" chord — feels like a starting bell.
        beep: () => {
            tone({ freq: 523, ms: 120, volume: 0.55 });
            setTimeout(() => tone({ freq: 659, ms: 120, volume: 0.6 }), 130);
            setTimeout(
                () => tone({ freq: 784, ms: 260, type: 'triangle', volume: 0.75 }),
                270
            );
        },
        // Caller passes target=<full sentence> for the welcome message.
        voice: (text) =>
            speak(text || 'Workout begins.', { rate: 1.0, volume: 0.95 }),
    },
    exercise_transition: {
        // Soft 2-tone "carry on" chord (E → G) — quieter than session_start.
        beep: () => {
            tone({ freq: 659, ms: 110, volume: 0.5 });
            setTimeout(
                () => tone({ freq: 880, ms: 200, type: 'triangle', volume: 0.65 }),
                120
            );
        },
        // Caller passes target=<full sentence> for the next-up message.
        voice: (text) =>
            speak(text || 'Next exercise.', { rate: 1.0, volume: 0.95 }),
    },
};

export function cue(type, { mode, target } = {}) {
    const m = mode || _state.mode;
    if (!m || m === 'off') return;
    const entry = CUE_TABLE[type];
    if (!entry) return;
    if (m === 'beeps') entry.beep?.();
    else if (m === 'voice') entry.voice?.(target);
}

/* -------------------- Schedule helper -------------------- */

/**
 * Given an elapsed second tick and a target, returns the cue id that should
 * fire on this tick, or null. Caller is responsible for de-duping (firing each
 * cue at most once per rest period).
 */
export function cueForTick(elapsed, target) {
    if (!target || target <= 0) return null;
    const e = Math.max(0, Math.floor(elapsed));
    const remaining = target - e;
    if (remaining === 15) return 'countdown_15';
    if (remaining === 10) return 'countdown_10';
    if (remaining === 5) return 'countdown_5';
    if (remaining === 3) return 'countdown_3';
    if (remaining === 2) return 'countdown_2';
    if (remaining === 1) return 'countdown_1';
    if (remaining === 0) return 'go';
    return null;
}
